import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ListChecks, Brain, Wrench, CheckCircle2, XCircle,
  Loader2, ChevronDown, ChevronRight, Circle,
} from 'lucide-react';

// Only allow safe link protocols in reasoning markdown (mirrors ChatTypingIndicator).
const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const sanitizeHref = (href) => (href && SAFE_HREF_RE.test(href) ? href : '#');

// Steps carry ts as epoch-seconds float. Render HH:MM:SS local, or "" if absent.
const fmtTs = (ts) => {
  if (typeof ts !== 'number' || !isFinite(ts)) return '';
  const dt = new Date(ts * 1000);
  if (isNaN(dt.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getHours())}:${p(dt.getMinutes())}:${p(dt.getSeconds())}`;
};

// Self-contained markdown for a step body. Not the in-file ChatMessage
// MarkdownRenderer (unexported + would create a circular import).
const StepMarkdown = ({ content, d }) => (
  <div className={`text-sm leading-relaxed break-words ${d ? 'text-slate-300' : 'text-gray-700'}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, ...p }) => (
          <a {...p} href={sanitizeHref(href)} target="_blank" rel="noopener noreferrer"
             className={d ? 'text-cyan-400 underline' : 'text-cyan-600 underline'}>{children}</a>
        ),
        code: ({ children, ...p }) => (
          <code {...p} className={`px-1 py-0.5 rounded text-xs font-mono ${d ? 'bg-slate-700 text-slate-200' : 'bg-gray-100 text-gray-800'}`}>{children}</code>
        ),
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="list-disc ml-4 mb-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal ml-4 mb-1">{children}</ol>,
      }}
    >
      {typeof content === 'string' ? content : String(content ?? '')}
    </ReactMarkdown>
  </div>
);
StepMarkdown.propTypes = { content: PropTypes.any, d: PropTypes.bool };

const ReasoningSteps = ({ steps = [], isStreaming = false, isDarkMode = false }) => {
  const d = isDarkMode;
  const [open, setOpen] = useState(isStreaming);
  const [expandedIdx, setExpandedIdx] = useState(null);

  // Auto-expand while streaming so live steps are visible; a user's collapse
  // choice sticks once the stream finishes (history reload starts collapsed).
  useEffect(() => { if (isStreaming) setOpen(true); }, [isStreaming]);

  if (!Array.isArray(steps) || steps.length === 0) return null;

  const border   = d ? 'border-slate-700/60'   : 'border-gray-200';
  const panelBg  = d ? 'bg-slate-800/40'        : 'bg-gray-50/80';
  const headerBg = d ? 'hover:bg-slate-700/50'  : 'hover:bg-gray-50';
  const sub      = d ? 'text-slate-400'         : 'text-gray-500';
  const heading  = d ? 'text-slate-300'         : 'text-gray-600';
  const rowHover = d ? 'hover:bg-slate-700/40'   : 'hover:bg-gray-100/70';

  const okChip     = d ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const failedChip = d ? 'bg-red-500/20 text-red-300 border-red-500/30'             : 'bg-red-50 text-red-600 border-red-200';
  const errText    = d ? 'text-red-300' : 'text-red-600';
  const spinner    = d ? 'text-sky-400' : 'text-blue-400';

  const renderRow = (step, i) => {
    const running = isStreaming && i === steps.length - 1;
    const ts = fmtTs(step.ts);

    // ----- status: muted single-line label, no expand -----
    if (step.type === 'status') {
      return (
        <div key={i} className="flex items-center gap-2 px-3 py-1.5">
          {running
            ? <Loader2 className={`w-3.5 h-3.5 shrink-0 animate-spin ${spinner}`} />
            : <Circle className={`w-3 h-3 shrink-0 ${sub}`} />}
          <span className={`text-xs ${sub} min-w-0 break-words`}>{step.content}</span>
          {ts && <span className={`ml-auto text-[10px] font-mono tabular-nums shrink-0 ${sub}`}>{ts}</span>}
        </div>
      );
    }

    // ----- tool: name + ok/failed chip, expandable body -----
    if (step.type === 'tool') {
      const failed = typeof step.status === 'string' && step.status.startsWith('failed');
      const ok = step.status === 'ok';
      const errTail = failed ? step.status.replace(/^failed:?\s*/, '') : '';
      const Icon = failed ? XCircle : (ok ? CheckCircle2 : Wrench);
      const iconCls = failed ? errText : (ok ? (d ? 'text-emerald-400' : 'text-emerald-600') : sub);
      const isExpanded = expandedIdx === i;
      return (
        <div key={i} className={`rounded-md overflow-hidden ${failed ? (d ? 'border border-red-700/40' : 'border border-red-300') : ''}`}>
          <button type="button" aria-expanded={isExpanded}
            onClick={() => setExpandedIdx(isExpanded ? null : i)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${rowHover}`}>
            <Icon className={`w-3.5 h-3.5 shrink-0 ${iconCls}`} />
            <span className={`text-xs font-medium min-w-0 truncate ${heading}`}>{step.tool}</span>
            {(ok || failed) && (
              <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md border shrink-0 ${failed ? failedChip : okChip}`}>
                {failed ? 'failed' : 'ok'}
              </span>
            )}
            {ts && <span className={`text-[10px] font-mono tabular-nums shrink-0 ${sub}`}>{ts}</span>}
            {isExpanded ? <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 ${sub}`} />
                        : <ChevronRight className={`w-3.5 h-3.5 ml-auto shrink-0 ${sub}`} />}
          </button>
          {isExpanded && (
            <div className={`px-3 pb-2 pt-1 border-t ${border} ${panelBg} space-y-1`}>
              {errTail && <p className={`text-xs ${errText}`}>{errTail}</p>}
              {step.content && <StepMarkdown content={step.content} d={d} />}
            </div>
          )}
        </div>
      );
    }

    // ----- thought (default): truncated label, expand for full markdown -----
    const isExpanded = expandedIdx === i;
    return (
      <div key={i} className="rounded-md overflow-hidden">
        <button type="button" aria-expanded={isExpanded}
          onClick={() => setExpandedIdx(isExpanded ? null : i)}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${rowHover}`}>
          <Brain className={`w-3.5 h-3.5 shrink-0 ${d ? 'text-indigo-300' : 'text-indigo-600'}`} />
          <span className={`text-xs min-w-0 truncate ${heading}`}>
            {typeof step.content === 'string' ? step.content : ''}
          </span>
          {running && <Loader2 className={`w-3.5 h-3.5 shrink-0 animate-spin ${spinner}`} />}
          {ts && <span className={`text-[10px] font-mono tabular-nums shrink-0 ${sub}`}>{ts}</span>}
          {isExpanded ? <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 ${sub}`} />
                      : <ChevronRight className={`w-3.5 h-3.5 ml-auto shrink-0 ${sub}`} />}
        </button>
        {isExpanded && (
          <div className={`px-3 pb-2 pt-1 border-t ${border} ${panelBg}`}>
            <StepMarkdown content={step.content} d={d} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`mb-2 rounded-xl border overflow-hidden ${border} ${panelBg}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2 transition-colors ${headerBg}`}>
        <ListChecks className={`w-4 h-4 shrink-0 ${sub}`} />
        <span className={`text-xs font-semibold uppercase tracking-wider ${heading}`}>
          Reasoning ({steps.length} step{steps.length === 1 ? '' : 's'})
        </span>
        {isStreaming && <Loader2 className={`w-3.5 h-3.5 shrink-0 animate-spin ${spinner}`} />}
        {open ? <ChevronDown className={`w-4 h-4 ml-auto shrink-0 ${sub}`} />
              : <ChevronRight className={`w-4 h-4 ml-auto shrink-0 ${sub}`} />}
      </button>
      {open && <div className={`border-t ${border} p-1.5 space-y-1`}>{steps.map(renderRow)}</div>}
    </div>
  );
};

ReasoningSteps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['thought', 'tool', 'status']),
    content: PropTypes.string,
    tool: PropTypes.string,
    status: PropTypes.string,
    ts: PropTypes.number,
  })),
  isStreaming: PropTypes.bool,
  isDarkMode: PropTypes.bool,
};

export default ReasoningSteps;
